import { useEffect, useState } from 'react';
import { LocalTrackPublication, Participant, RemoteTrackPublication } from 'twilio-video';

type TrackPublication = LocalTrackPublication | RemoteTrackPublication;

const getPublicationId = (publication: TrackPublication) =>
  publication.trackSid ?? `${publication.kind}-${publication.trackName}`;

const dedupePublications = (publications: TrackPublication[]) =>
  Array.from(new Map(publications.map(publication => [getPublicationId(publication), publication])).values());

export default function usePublications(participant: Participant) {
  const [publications, setPublications] = useState<TrackPublication[]>(() =>
    dedupePublications(Array.from(participant.tracks.values()) as TrackPublication[])
  );

  useEffect(() => {
    // Reset the publications when the 'participant' variable changes.
    setPublications(dedupePublications(Array.from(participant.tracks.values()) as TrackPublication[]));

    const publicationAdded = (publication: TrackPublication) =>
      setPublications(prevPublications => dedupePublications([...prevPublications, publication]));
    const publicationRemoved = (publication: TrackPublication) =>
      setPublications(prevPublications =>
        prevPublications.filter(p => getPublicationId(p) !== getPublicationId(publication))
      );

    participant.on('trackPublished', publicationAdded);
    participant.on('trackUnpublished', publicationRemoved);
    return () => {
      participant.off('trackPublished', publicationAdded);
      participant.off('trackUnpublished', publicationRemoved);
    };
  }, [participant]);

  return publications;
}
